import { useState } from "react";
import { Dropdown, Spinner } from "flowbite-react";
import { HiDownload } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast";
import { exportList, type ExportColumn, type ExportFormat } from "@/utils/listExport";
import { useCurrentUser } from "@/hooks/useCurrentUser";

interface ExportMenuProps<T> {
  /** Heading written into the file. */
  title: string;
  /** File name without extension. */
  filename: string;
  columns: ExportColumn<T>[];
  /** Active filters, already turned into labels the reader will recognise. */
  filters?: Array<{ label: string; value: string }>;
  /**
   * Every row matching the current filters — not just the page on screen.
   * The caller fetches them, since only it knows its own query.
   */
  fetchRows: () => Promise<T[]>;
  disabled?: boolean;
}

const FORMATS: Array<{ key: ExportFormat; labelKey: string }> = [
  { key: "xlsx", labelKey: "common:exportExcel" },
  { key: "csv", labelKey: "common:exportCsv" },
  { key: "pdf", labelKey: "common:exportPdf" },
];

export default function ExportMenu<T>({
  title,
  filename,
  columns,
  filters,
  fetchRows,
  disabled,
}: ExportMenuProps<T>) {
  const { t, i18n } = useTranslation(["common"]);
  const [busy, setBusy] = useState(false);
  const currentUser = useCurrentUser();

  // Taken at the moment of export, in the browser's zone, and named — so the
  // file says whose clock its timestamp is on.
  const exportMeta = () => {
    const now = new Date();
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offset =
      new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longOffset" })
        .formatToParts(now)
        .find((p) => p.type === "timeZoneName")
        ?.value.replace("GMT", "UTC") || "UTC";
    const name = currentUser?.display_name || currentUser?.agent_username || "-";
    return [
      {
        label: t("common:exportGeneratedBy"),
        value: currentUser?.agent_id ? `${name} (Agent #${currentUser.agent_id})` : name,
      },
      { label: t("common:exportGeneratedAt"), value: now.toLocaleString(i18n.language) },
      { label: t("common:exportTimeZone"), value: `${zone} (${offset === "UTC" ? "UTC+00:00" : offset})` },
    ];
  };

  const run = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(true);
    try {
      const rows = await fetchRows();
      if (!rows.length) {
        toast.warn(t("common:exportEmpty"));
        return;
      }
      // Date in the name so repeated exports don't overwrite each other.
      const stamp = new Date().toISOString().slice(0, 10);
      await exportList(format, {
        rows,
        columns,
        filename: `${filename}-${stamp}`,
        title,
        filters,
        meta: exportMeta(),
      });
    } catch (err: any) {
      console.error("export failed", err);
      toast.error(t("common:exportFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dropdown
      color="light"
      disabled={disabled || busy}
      label={
        <span className="flex items-center gap-2">
          {busy ? <Spinner size="sm" /> : <HiDownload className="h-4 w-4" />}
          {t("common:export")}
        </span>
      }
      arrowIcon={false}
    >
      {FORMATS.map((f) => (
        <Dropdown.Item key={f.key} onClick={() => run(f.key)}>
          {t(f.labelKey)}
        </Dropdown.Item>
      ))}
    </Dropdown>
  );
}
